import { queryArcgisRest } from '../arcgis/queryArcgisRest';
import { isCriticalHabitatSourceClosure, USFWS_CH_FIELDS, USFWS_CH_LAYERS, USFWS_CH_SERVICE } from '../refdata/usfwsCriticalHabitatContract';
import type { CriticalHabitatCoverage, CriticalHabitatFinding, RefdataResultMeta } from '../../types/refdata';
import { isPrivateRefdataAcceptanceDataset } from '../refdata/refdataClient';

type Attrs = Record<string, unknown>;
export interface HabitatSnapshot {
  attrs: Attrs[];
  meta: RefdataResultMeta;
  truncated: boolean;
  vintage: string;
}
const string = (v: unknown): string | undefined => typeof v === 'string' && v.trim() ? v.trim() : undefined;
function finding(attrs: Attrs, layer?: typeof USFWS_CH_LAYERS[number]): CriticalHabitatFinding {
  return {
    designation: layer?.designation ?? 'unknown',
    species: string(attrs.comname) ?? string(attrs.sciname) ?? 'Species name unavailable',
    sourceUrl: layer?.sourceUrl ?? USFWS_CH_SERVICE,
    ...(layer ? { sourceLayerId: layer.layerId } : {}),
    ...(attrs.OBJECTID != null || attrs.sourceFeatureId != null ? { sourceFeatureId: String(attrs.sourceFeatureId ?? attrs.OBJECTID) } : {}),
    ...(string(attrs.listing_status) ? { listingStatus: string(attrs.listing_status) } : {}),
    ...(string(attrs.status) ? { status: string(attrs.status) } : {}),
    ...(string(attrs.pubdate) ? { publicationDate: string(attrs.pubdate) } : {}),
    ...(string(attrs.effectdate) ? { effectiveDate: string(attrs.effectdate) } : {}),
    ...(string(attrs.vacatedate) ? { vacatedDate: string(attrs.vacatedate) } : {}),
  };
}

/** A complete mapped screen is not a species survey or an ESA consultation decision. */
export async function screenCriticalHabitat(local: HabitatSnapshot | null, geometryParams: string,
  queryScope: CriticalHabitatCoverage['queryScope'], signal?: AbortSignal) {
  const closure = local && isCriticalHabitatSourceClosure(local.meta.criticalHabitatSources, local.meta.version);
  const identified = (attrs: Attrs) => USFWS_CH_LAYERS.find(layer =>
    attrs.sourceLayerId === layer.layerId && attrs.designation === layer.designation && attrs.sourceUrl === layer.sourceUrl);
  const localComplete = !!local && !!closure && !local.truncated && local.attrs.every(attrs => identified(attrs));
  let findings: CriticalHabitatFinding[];
  let layers: CriticalHabitatCoverage['layers'];
  if (localComplete) {
    findings = local!.attrs.map(attrs => finding(attrs, identified(attrs)));
    layers = USFWS_CH_LAYERS.map(layer => ({ ...layer, status: 'complete' }));
  } else if (isPrivateRefdataAcceptanceDataset('usfws-critical-habitat')) {
    findings = (local?.attrs ?? []).map(attrs => finding(attrs, closure ? identified(attrs) : undefined));
    layers = USFWS_CH_LAYERS.map(layer => ({ ...layer, status: local ? 'partial' : 'unavailable',
      reason: 'The frozen acceptance query is incomplete; live designation supplementation is withheld.' }));
  } else {
    // Independent designation requests preserve positive evidence if the other
    // endpoint fails. Missing / legacy closure never certifies a negative.
    const results = await Promise.allSettled(USFWS_CH_LAYERS.map(async layer => {
      const json = await queryArcgisRest(`${layer.sourceUrl}/query?${geometryParams}&spatialRel=esriSpatialRelIntersects&outFields=${USFWS_CH_FIELDS.join(',')}&returnGeometry=false&resultRecordCount=1000&f=json`,
        { label: 'USFWS Critical Habitat', signal });
      if (!Array.isArray(json?.features) || json.features.some((f: { attributes?: unknown }) => !f?.attributes || typeof f.attributes !== 'object')) {
        throw new Error(`USFWS Critical Habitat layer ${layer.layerId} returned malformed features`);
      }
      return { findings: json.features.map(f => finding(f.attributes!, layer)),
        truncated: json.exceededTransferLimit === true || json.features.length >= 1000 };
    }));
    signal?.throwIfAborted();
    findings = results.flatMap(result => result.status === 'fulfilled' ? result.value.findings : []);
    layers = results.map((result, i) => ({ ...USFWS_CH_LAYERS[i]!,
      status: result.status === 'rejected' ? 'unavailable' : result.value.truncated ? 'partial' : 'complete',
      ...(result.status === 'rejected' ? { reason: result.reason instanceof Error ? result.reason.message : String(result.reason) }
        : result.value.truncated ? { reason: 'Feature query reached its transfer limit.' } : {}),
    }));
    if (layers.some(layer => layer.status !== 'complete')) {
      findings.push(...(local?.attrs ?? []).map(attrs => finding(attrs, closure ? identified(attrs) : undefined)));
    }
  }
  findings = [...new Map(findings.map(f => [JSON.stringify(f), f])).values()];
  const complete = layers.every(layer => layer.status === 'complete');
  const coverage: CriticalHabitatCoverage = {
    status: complete ? 'complete' : layers.some(layer => layer.status !== 'unavailable') ? 'partial' : 'unknown',
    queryScope, layers,
    reason: complete ? 'Both final and proposed designation layers were screened completely within the query footprint; this is not a species presence/absence survey.'
      : 'Critical habitat absence is unresolved: ' + layers.filter(layer => layer.status !== 'complete').map(layer => `${layer.designation}: ${layer.reason}`).join('; '),
  };
  const hasCriticalHabitat = findings.length ? true : complete ? false : undefined;
  const species = [...new Set(findings.map(f => f.species))];
  const detail = findings.length
    ? `${species.length} species associated with mapped critical habitat: ${findings.map(f => `${f.species} (${f.designation})`).join(', ')}`
    : complete ? 'No mapped final or proposed critical habitat in the screened query' : 'Critical habitat absence unresolved';
  return { hasCriticalHabitat, species, criticalHabitatFindings: findings, criticalHabitatCoverage: coverage,
    detail: `${detail}${localComplete ? ` [local snapshot, vintage ${local!.vintage}]` : ''}. ${coverage.reason}` };
}
